// src/services/emailTemplates.ts
//
// Lightweight HTML templates for transactional emails.
// Kept inline + self-contained so they render the same in any email client.
// No external CSS, no remote images — everything inlined.

const BRAND_COLOR = '#1d4ed8';     // tailwind blue-700
const BRAND_ACCENT = '#f3f4f6';    // tailwind gray-100
const TEXT_COLOR = '#111827';      // tailwind gray-900
const MUTED_COLOR = '#6b7280';     // tailwind gray-500
const BORDER_COLOR = '#e5e7eb';    // tailwind gray-200

function shell(bodyHtml: string, preheader: string): string {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
  </head>
  <body style="margin:0;padding:0;background-color:${BRAND_ACCENT};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:${TEXT_COLOR};">
    <!-- Preheader text, hidden in most clients -->
    <div style="display:none;overflow:hidden;line-height:1;max-height:0;max-width:0;opacity:0;visibility:hidden;mso-hide:all;">
      ${preheader}
    </div>
    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color:${BRAND_ACCENT};">
      <tr>
        <td align="center" style="padding:32px 16px;">
          <table role="presentation" width="560" border="0" cellspacing="0" cellpadding="0" style="max-width:560px;background-color:#ffffff;border:1px solid ${BORDER_COLOR};border-radius:8px;">
            <tr>
              <td style="padding:24px 32px;border-bottom:1px solid ${BORDER_COLOR};">
                <div style="font-size:14px;font-weight:600;color:${BRAND_COLOR};letter-spacing:0.5px;text-transform:uppercase;">
                  PIT — Salesian College Sunbury
                </div>
              </td>
            </tr>
            <tr>
              <td style="padding:32px;">
                ${bodyHtml}
              </td>
            </tr>
            <tr>
              <td style="padding:16px 32px 24px;border-top:1px solid ${BORDER_COLOR};font-size:12px;color:${MUTED_COLOR};">
                This is an automated message from PIT. If you have questions, reply to this email.
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
}

function button(href: string, label: string): string {
  return `<a href="${href}" style="display:inline-block;background-color:${BRAND_COLOR};color:#ffffff;padding:10px 18px;border-radius:6px;text-decoration:none;font-weight:600;">${label}</a>`;
}

/**
 * Build the "Your load letter is available" email to a teacher.
 */
export function loadLetterReleasedTemplate(params: {
  teacherFirstName: string;
  semesterLabel: string;   // e.g. "Semester 1, 2026"
  appUrl: string;
}): { subject: string; html: string; text: string } {
  const { teacherFirstName, semesterLabel, appUrl } = params;
  const dashboardUrl = `${appUrl.replace(/\/$/, '')}/load-letter/staff-dashboard`;
  const subject = `Your load letter for ${semesterLabel} is ready to view`;
  const html = shell(
    `
    <h1 style="font-size:22px;margin:0 0 16px 0;">Your load letter is ready</h1>
    <p style="margin:0 0 16px 0;font-size:15px;line-height:1.5;">
      Hi ${teacherFirstName},
    </p>
    <p style="margin:0 0 16px 0;font-size:15px;line-height:1.5;">
      Your teaching load letter for <strong>${semesterLabel}</strong> has been released. You can review your allocated classes, FTE, and download a PDF copy from your staff dashboard.
    </p>
    <div style="margin:24px 0;">
      ${button(dashboardUrl, 'View my load letter')}
    </div>
    <p style="margin:0;font-size:13px;color:${MUTED_COLOR};line-height:1.5;">
      If the button doesn't work, copy this into your browser:<br />
      <span style="word-break:break-all;">${dashboardUrl}</span>
    </p>
    `,
    `Your ${semesterLabel} teaching load letter is ready to view.`
  );
  const text = `Hi ${teacherFirstName},

Your teaching load letter for ${semesterLabel} has been released.

View it here: ${dashboardUrl}

This is an automated message from PIT.`;
  return { subject, html, text };
}

/**
 * Build the "Teacher has requested a discussion about their FTE/day-off preferences" email to the HR leader.
 */
export function hrDiscussFlagTemplate(params: {
  recipientFirstName: string;      // "Alison"
  teacherName: string;
  teacherStaffCode: string;
  department: string;
  year: number;                    // academic year of intention
  reasonSummary: string;           // e.g. "FTE preference" / "Day-off preference" / "FTE + day-off"
  appUrl: string;
  intentionId: string;
}): { subject: string; html: string; text: string } {
  const { recipientFirstName, teacherName, teacherStaffCode, department, year, reasonSummary, appUrl, intentionId } = params;
  const queueUrl = `${appUrl.replace(/\/$/, '')}/hr/discuss-queue?highlight=${encodeURIComponent(intentionId)}`;
  const subject = `${teacherName} has requested a discussion about their ${year} intentions`;
  const html = shell(
    `
    <h1 style="font-size:22px;margin:0 0 16px 0;">A teacher wants to discuss their load</h1>
    <p style="margin:0 0 16px 0;font-size:15px;line-height:1.5;">
      Hi ${recipientFirstName},
    </p>
    <p style="margin:0 0 16px 0;font-size:15px;line-height:1.5;">
      <strong>${teacherName}</strong> (${teacherStaffCode}, ${department || 'no department'}) has submitted their ${year} teaching intentions and chose <em>"I'd like to discuss my options"</em> for their <strong>${reasonSummary}</strong>.
    </p>
    <p style="margin:0 0 16px 0;font-size:15px;line-height:1.5;">
      When you're ready, reach out to them to talk through what would work best.
    </p>
    <div style="margin:24px 0;">
      ${button(queueUrl, 'Open the discuss queue')}
    </div>
    <p style="margin:0;font-size:13px;color:${MUTED_COLOR};line-height:1.5;">
      If the button doesn't work, copy this into your browser:<br />
      <span style="word-break:break-all;">${queueUrl}</span>
    </p>
    `,
    `${teacherName} wants to discuss their ${reasonSummary}.`
  );
  const text = `Hi ${recipientFirstName},

${teacherName} (${teacherStaffCode}, ${department || 'no department'}) has submitted their ${year} teaching intentions and selected "I'd like to discuss my options" for their ${reasonSummary}.

Open the discuss queue: ${queueUrl}

This is an automated message from PIT.`;
  return { subject, html, text };
}
